import { useCallback, useEffect, useRef, useState } from "react";
import { ConversationOverrides, ConversationStatus, VoiceAgentService } from '@/services/integrations/elevenlabs/VoiceAgentService';
import { useAuth } from '@/auth/AuthProvider';
import type { ContactDetailsRequestHandler } from '@/types/voice/summary-capture';

export interface VoiceAgentState {
  speaking: boolean;
  listening: boolean;
  thinking: boolean;
  processing: boolean;
  searching: boolean;
  retrieving: boolean;
  connected: boolean;
  isConnecting: boolean;
  isMuted: boolean;
}

export interface VoiceAgentActions {
  startConversation: () => Promise<boolean>;
  endConversation: () => Promise<void>;
  getConversationId: () => string | null;
  sendUserMessage: (text: string) => void;
  sendContextualUpdate: (text: string) => void;
  getService: () => VoiceAgentService | null;
  onDisconnect: (callback: (conversationId: string | null) => Promise<void>) => void;
  onHumanHandoffRequested: (callback: () => void) => void;
  onContactDetailsRequested: (callback: ContactDetailsRequestHandler | null) => void;
  mute: () => Promise<void>;
  unmute: () => Promise<void>;
  toggleMute: () => Promise<boolean>;
  muteMicrophoneOnly: () => Promise<void>;
  unmuteMicrophoneOnly: () => Promise<void>;
  setOverrides: (overrides: ConversationOverrides | undefined) => void;
}

export interface UseVoiceAgentOptions {
  overrides?: ConversationOverrides;
  userMemory?: string | null;
  isQuickTest?: boolean;
  /** Language the genie must always answer in (multi-language off); omit to follow the caller. */
  responseLanguage?: string | null;
}

export const useVoiceAgent = (agentId?: string, options?: UseVoiceAgentOptions) => {
  const { userProfile } = useAuth();
  const [state, setState] = useState<VoiceAgentState>({
    speaking: false,
    listening: false,
    thinking: false,
    processing: false,
    searching: false,
    retrieving: false,
    connected: false,
    isConnecting: false,
    isMuted: false,
  });

  const serviceRef = useRef<VoiceAgentService | null>(null);

  // Store overrides in a ref to avoid recreating service on every render
  // The overrides are only used when creating the service, not during the conversation
  const overridesRef = useRef(options?.overrides);
  overridesRef.current = options?.overrides;
  // Kept here, not only on the service, so a service recreated for a new
  // signed-in user still answers the Genie's requestContactDetails tool.
  const contactDetailsHandlerRef = useRef<ContactDetailsRequestHandler | null>(null);

  useEffect(() => {
    if (!agentId) return;

    setState((prev) => ({ ...prev, isConnecting: false }));
    const service = new VoiceAgentService(agentId, userProfile?.id, userProfile?.email, userProfile?.full_name, overridesRef.current, options?.userMemory, options?.isQuickTest);
    service.setResponseLanguage(options?.responseLanguage ?? null);
    service.setOnContactDetailsRequested(contactDetailsHandlerRef.current);
    serviceRef.current = service;

    service.onModeChange((mode) => {
      const isActiveMode = mode === "speaking" || mode === "listening" || mode === "thinking" ||
        mode === "processing" || mode === "searching" || mode === "retrieving";
      setState((prev) => ({
        ...prev,
        speaking: mode === "speaking",
        listening: mode === "listening",
        thinking: mode === "thinking",
        processing: mode === "processing",
        searching: mode === "searching",
        retrieving: mode === "retrieving",
        isConnecting: isActiveMode ? false : prev.isConnecting,
      }));
    });

    service.onStatusChange((status: ConversationStatus) => {
      setState((prev) => ({
        ...prev,
        connected: status === "connected",
        isConnecting: status === "connecting",
        speaking: status === "disconnected" ? false : prev.speaking,
        listening: status === "disconnected" ? false : prev.listening,
        thinking: status === "disconnected" ? false : prev.thinking,
        processing: status === "disconnected" ? false : prev.processing,
        searching: status === "disconnected" ? false : prev.searching,
        retrieving: status === "disconnected" ? false : prev.retrieving,
        isMuted: status === "disconnected" ? false : prev.isMuted,
      }));
    });

    service.onMuteChange((isMuted: boolean) => {
      setState((prev) => ({
        ...prev,
        isMuted,
      }));
    });

    // Initialize state from service
    const currentMode = service.getMode();
    setState((prev) => ({
      ...prev,
      speaking: currentMode === "speaking",
      listening: currentMode === "listening",
      thinking: currentMode === "thinking",
      processing: currentMode === "processing",
      searching: currentMode === "searching",
      retrieving: currentMode === "retrieving",
      connected: service.getStatus() === "connected",
      isMuted: service.isMuted(),
    }));

    return () => {
      if (serviceRef.current) {
        serviceRef.current.onDisconnect(async () => undefined);
        serviceRef.current.endConversation();
        serviceRef.current = null;
      }
      setState({
        speaking: false,
        listening: false,
        thinking: false,
        processing: false,
        searching: false,
        retrieving: false,
        connected: false,
        isConnecting: false,
        isMuted: false,
      });
    };
  // Note: overrides and userMemory are stored in ref, so we don't include them in deps
  // The service is only recreated when agentId or user info changes
  }, [agentId, userProfile?.id, userProfile?.email, userProfile?.full_name]);

  const startConversation = useCallback(async (): Promise<boolean> => {
    if (!serviceRef.current || state.isConnecting) return false;

    setState((prev) => ({ ...prev, isConnecting: true }));
    try {
      const success = await serviceRef.current.startConversation();
      if (!success) {
        setState((prev) => ({ ...prev, isConnecting: false }));
      }
      return success;
    } catch (error) {
      setState((prev) => ({ ...prev, isConnecting: false }));
      throw error;
    }
  }, [state.isConnecting]);

  const endConversation = useCallback(async (): Promise<void> => {
    if (!serviceRef.current) return;

    await serviceRef.current.endConversation();
    setState((prev) => ({ ...prev, isConnecting: false }));
  }, []);

  const getConversationId = useCallback((): string | null => {
    return serviceRef.current?.getConversationId() || null;
  }, []);

  const sendUserMessage = useCallback((text: string): void => {
    if (serviceRef.current) {
      serviceRef.current.sendUserMessage(text);
    }
  }, []);

  const sendContextualUpdate = useCallback((text: string): void => {
    if (serviceRef.current) {
      serviceRef.current.sendContextualUpdate(text);
    }
  }, []);

  const getService = useCallback((): VoiceAgentService | null => {
    return serviceRef.current;
  }, []);

  const onDisconnect = useCallback((callback: (conversationId: string | null) => Promise<void>) => {
    if (serviceRef.current) {
      serviceRef.current.onDisconnect(callback);
    }
  }, []);

  const onHumanHandoffRequested = useCallback((callback: () => void) => {
    if (serviceRef.current) {
      serviceRef.current.setOnHumanHandoffRequested(callback);
    }
  }, []);

  const onContactDetailsRequested = useCallback((callback: ContactDetailsRequestHandler | null) => {
    contactDetailsHandlerRef.current = callback;
    serviceRef.current?.setOnContactDetailsRequested(callback);
  }, []);

  const mute = useCallback(async (): Promise<void> => {
    if (serviceRef.current) {
      await serviceRef.current.mute();
    }
  }, []);

  const unmute = useCallback(async (): Promise<void> => {
    if (serviceRef.current) {
      await serviceRef.current.unmute();
    }
  }, []);

  const toggleMute = useCallback(async (): Promise<boolean> => {
    if (serviceRef.current) {
      return await serviceRef.current.toggleMute();
    }
    return false;
  }, []);

  const muteMicrophoneOnly = useCallback(async (): Promise<void> => {
    if (serviceRef.current) {
      await serviceRef.current.muteMicrophoneOnly();
    }
  }, []);

  const unmuteMicrophoneOnly = useCallback(async (): Promise<void> => {
    if (serviceRef.current) {
      await serviceRef.current.unmuteMicrophoneOnly();
    }
  }, []);

  const setOverrides = useCallback((newOverrides: ConversationOverrides | undefined) => {
    if (serviceRef.current) {
      serviceRef.current.setOverrides(newOverrides);
    }
  }, []);

  const actions: VoiceAgentActions = {
    startConversation,
    endConversation,
    getConversationId,
    sendUserMessage,
    sendContextualUpdate,
    getService,
    onDisconnect,
    onHumanHandoffRequested,
    onContactDetailsRequested,
    mute,
    unmute,
    toggleMute,
    muteMicrophoneOnly,
    unmuteMicrophoneOnly,
    setOverrides,
  };

  return {
    ...state,
    ...actions,
  };
};
